import { SuccessCheckAnimation } from '@/components/animations/success-check-animation';
import { Pod } from '@/components/pod';
import RewardsDialogBase from '@/components/rewards-dialog-base';
import { MaxColumnsContainer } from '@/components/ui/layout';
import commonMessages from '@/lang/messages/common';
import programMessages from '@/lang/messages/program';
import { SimpleDialogProps } from '@/libs/material/simple-dialog';
import { SimpleDialogTitle } from '@/libs/material/simple-dialog/title';
import { EnumDialogName } from '@calm/evexia-analytics';
import {
    RewardPartnerRewardDetail,
    RewardRewardsDetailsResponse,
} from '@calm/evexia-web-sdk';
import { PlayArrow } from '@mui/icons-material';
import {
    Box,
    Button,
    Grid,
    SxProps,
    Theme,
    Typography,
} from '@rhg/libs/material';
import { useIntl } from 'react-intl';
import { JoinDialogTrackProps } from '..';
import RewardItem from './RewardItem';
import messages from './messages';

export interface JoinDialogOptions {
    trackDialog?: EnumDialogName;
    title?: string;
    sx?: SxProps<Theme>;
    SimpleDialogProps?: Partial<
        Omit<SimpleDialogProps, 'iconContainerOverride'>
    >;
    onPlayClick: () => void;
    rewardsDetails?: RewardRewardsDetailsResponse;
}

const styleGridContainer = {
    justifyContent: 'space-evenly',
    mt: 6,
};

const stylePod = {
    my: 4,
    px: {
        xs: 4,
        lg: 6,
    },
};
const stylePodSection = {
    py: 6,
    width: '100%',
};

interface JoinDialogProps {
    isOpen: boolean;
    onClose: () => void;
    skipDialogViewedTracking?: boolean;
    options?: {
        title?: string;
        rewardsDetails?: RewardRewardsDetailsResponse;
        sx?: SxProps<Theme>;
        trackProps?: JoinDialogTrackProps;
    };
    handleRewardClick: (reward: RewardPartnerRewardDetail) => void;
    handleClick: () => void;
}

const JoinDialog = ({
    isOpen,
    onClose,
    skipDialogViewedTracking,
    options,
    handleRewardClick,
    handleClick,
}: JoinDialogProps) => {
    const { formatMessage } = useIntl();

    return (
        <RewardsDialogBase
            isOpen={isOpen}
            onClose={onClose}
            trackDialog={EnumDialogName.ProgramJoinDialog}
            trackProps={{
                AvailableRewards: options?.rewardsDetails?.rewards,
                NumberOfAvailableRewards:
                    options?.rewardsDetails?.rewards.length,
                ProgramId: options?.trackProps?.programId,
                ProgramName: options?.trackProps?.programName,
            }}
            skipDialogViewedTracking={skipDialogViewedTracking}
            hideCloseButton
            icon={<SuccessCheckAnimation />}
            noIconPadding
            iconColor="info"
            sx={options?.sx}
            dialogButton={
                <Button
                    startIcon={<PlayArrow />}
                    onClick={handleClick}
                    variant="contained"
                    trackButton="ProgramPlay"
                    size="large"
                    fullWidth
                >
                    {formatMessage(
                        commonMessages['common.play_session_number'],
                        {
                            number: 1,
                        }
                    )}
                </Button>
            }
            dialogContent={
                <MaxColumnsContainer columns={6} gap={4}>
                    <Typography
                        variant="subtitle1"
                        color="text.sub"
                        textAlign="center"
                    >
                        {formatMessage(
                            programMessages['program.program_joined']
                        )}
                    </Typography>
                    <div>
                        <SimpleDialogTitle title={options?.title} />
                        <SimpleDialogTitle
                            title={formatMessage(messages['join.title'])}
                        />
                    </div>
                    <Typography
                        variant="body1"
                        color="text.sub"
                        textAlign="center"
                    >
                        {formatMessage(messages['join.subtitle'])}
                    </Typography>
                    <Pod
                        sx={stylePod}
                        sections={[
                            {
                                title: '',
                                rawSection: (
                                    <Box sx={stylePodSection}>
                                        <Typography
                                            color="text.body"
                                            variant="h5"
                                        >
                                            {formatMessage(
                                                messages[
                                                    'join.available.rewards'
                                                ]
                                            )}
                                        </Typography>
                                        <Grid
                                            container
                                            gap={2}
                                            sx={styleGridContainer}
                                        >
                                            {options?.rewardsDetails?.rewards.map(
                                                (reward) => (
                                                    <RewardItem
                                                        key={
                                                            reward.affiliatePartner
                                                        }
                                                        reward={reward}
                                                        handleRewardClick={
                                                            handleRewardClick
                                                        }
                                                        trackProps={
                                                            options?.trackProps
                                                        }
                                                    />
                                                )
                                            )}
                                        </Grid>
                                    </Box>
                                ),
                            },
                        ]}
                    />
                    <Typography
                        variant="caption"
                        color="text.sub"
                        textAlign="center"
                    >
                        {options?.rewardsDetails?.legal}
                    </Typography>
                </MaxColumnsContainer>
            }
        />
    );
};

export default JoinDialog;
